import React, { useState, useEffect, useCallback } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import { S3ClientForUserApplications } from "../../../utils/s3Config";
import FormAction from "./formAction";
import StatementOfFocus from "./statementOfFocus";
import { toastr } from "react-redux-toastr";
import DoctorMinistryEssay from "./doctorMinistryEssay";
import SampleMinistryPaper from "./sampleMinistryPaper";

function ApplicationEssay(props) {
  const { savedSteps, selectedProgramId, selectedProgramLabel } = useSelector(
    (state) => ({
      savedSteps: state.applicationReducer.savedSteps,
      selectedProgramId: state.applicationReducer.selectedProgramId,
      selectedProgramLabel: state.applicationReducer.selectedProgramLabel,
    }),
    shallowEqual
  );

  const [state, setState] = useState({
    formData: {
      percent_completed: 70,
      essay: "",
      statementOfFocus: "",
      doctorMinistryEssay: "",
      sampleMinistryPaper: "",
      programId: selectedProgramId,
    },
    essayValid: "",
    statementOfFocusValid: "",
    doctorMinistryEssayValid: "",
    sampleMinistryPaperValid: "",
    essayLoader: false,
    isFormDataValid: false,
    isEssayImage: false,
    isStatementImage: false,
    isDoctorEssayImage: false,
    isSampleMinistryPaperImage: false,
    essayFileName: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!!savedSteps.applicationEssay || !!savedSteps.statementOfFocus) {
      populateForm();
    }
  }, []);

  useEffect(() => {
    validateForm();
  }, [state]);

  const checkProgram = (id, bool) => {
    if (bool) return selectedProgramId === id;
    return selectedProgramId !== id;
  };

  function populateForm() {
    const applicationEssayData = savedSteps.applicationEssay;
    const statementData = savedSteps.statementOfFocus;
    const stepValid = !!statementData && !!applicationEssayData;
    if (
      (!!applicationEssayData &&
        Object.keys(applicationEssayData).length > 0) ||
      (!!statementData && Object.keys(statementData).length > 0)
    ) {
      savedSteps.applicationEssay;
      updateForm("essay", applicationEssayData.essay ?? "");
      updateForm(
        "statementOfFocus",
        applicationEssayData.statementOfFocus ?? ""
      );
      updateForm(
        "doctorMinistryEssay",
        applicationEssayData.doctorMinistryEssay ?? ""
      );
      updateForm(
        "sampleMinistryPaper",
        applicationEssayData.sampleMinistryPaper ?? ""
      );
    }
  }

  const updateForm = (field, value) => {
    let temp = state.formData;
    temp[field] = value;
    setState((prevState) => ({ ...prevState, formData: temp }));
    _validate(field);
  };

  const _validate = (field) => {
    let regex, valid;
    let value = state.formData[field];
    switch (field) {
      case "essay":
      case "statementOfFocus":
      case "doctorMinistryEssay":
      case "sampleMinistryPaper":
        regex = /^.{2,}/;
        valid = regex.test(value);
        break;
    }
    setState((prevState) => ({ ...prevState, [field + "Valid"]: valid }));
  };

  const validateForm = () => {
    let form =
      (checkProgram(48, true) || state.formData.essay?.length > 0) &&
      (checkProgram(42, true) || checkProgram(43, true) ||
        checkProgram(48, true) ||
        state.formData.statementOfFocus?.length > 0) &&
      (checkProgram(48, false) ||
        state.formData.doctorMinistryEssay?.length > 0) &&
      (checkProgram(48, false) ||
        state.formData.sampleMinistryPaper?.length > 0);
    if (state.isFormDataValid != form) {
      setState((prevState) => ({ ...prevState, isFormDataValid: form }));
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setState({ ...state, essayLoader: true });
    // Do something with the files
    if (acceptedFiles && acceptedFiles.length > 0) {
      let file = acceptedFiles[0];
      if (file.length === 0) {
        toastr.error("Error", "Please upload correct file");
        return;
      }
      if (file.size > 5000000) {
        toastr.error("Error", "Please upload file less than 5MB");
        return;
      }

      S3ClientForUserApplications.uploadFile(file, new Date().getTime())
        .then((data) => {
          updateForm("essay", data && data.location ? data.location : null);
          state.isEssayImage =
            file.type == "image/jpeg" || file.type == "image/png";
          state.essayFileName = file.name || "File";
          setState({ ...state, essayLoader: false });
        })
        .catch((err) => {
          toastr.error("Error", "Essay Uploading Fail");
          setState({ ...state, essayLoader: false });
        });
    } else {
      setState({ ...state, essayLoader: false });
      toastr.error("Error", "Invalid File Type. PDF only");
    }
  }, []);

  const removeFile = (type) => {
    if (type === "essay") {
      updateForm("essay", "");
    } else if (type === "doctorMinistryEssay") {
      updateForm("doctorMinistryEssay", "");
    } else if (type === "sampleMinistryPaper") {
      updateForm("sampleMinistryPaper", "");
    } else {
      updateForm("statementOfFocus", "");
    }
  };

  return (
    <>
      <div className="app-form-container demographic residency">
        <div className="main-heading bayan-main-heading">
          {selectedProgramLabel}
        </div>
        {checkProgram(48, false) && (
          <AppEssay
            state={state}
            onDrop={onDrop}
            removeFile={removeFile}
            updateForm={updateForm}
          />
        )}
        {checkProgram(42, false) && checkProgram(43, false) && checkProgram(48, false) && (
          <StatementOfFocus
            removeFile={removeFile}
            updateForm={updateForm}
            isStatementImage={state.isStatementImage}
            statementOfFocus={state.formData.statementOfFocus}
          />
        )}
        {checkProgram(48, true) && (
          <>
            <DoctorMinistryEssay
              removeFile={removeFile}
              updateForm={updateForm}
              isDoctorEssayImage={state.isDoctorEssayImage}
              doctorMinistryEssay={state.formData.doctorMinistryEssay}
            />
            <SampleMinistryPaper
              removeFile={removeFile}
              updateForm={updateForm}
              isSampleMinistryPaperImage={state.isSampleMinistryPaperImage}
              sampleMinistryPaper={state.formData.sampleMinistryPaper}
            />
          </>
        )}

        <FormAction
          isFormDataValid={state.isFormDataValid}
          formData={state.formData}
          formStep={"applicationEssay"}
          backStep={"references"}
          nextStep={"applicationFee"}
        />
      </div>
    </>
  );
}

function AppEssay({ state, onDrop, removeFile, updateForm }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".pdf",
  });

  return (
    <>
      <div className="sub-heading app-info">Application Essay</div>
      <div className="form-content">
        <div className="input-group">
          <div className="field-label">
            Education History
            <br />
            “Please submit an essay of 2000 words or less incorporating a
            response to the following questions:
          </div>

          <ol placeholder="Select">
            <li>
              Briefly describe what compels you to pursue religious studies.{" "}
            </li>
            <li>
              Describe your religious history and/or a personal experience that
              has led you to pursue your studies.{" "}
            </li>
            <li>Share your educational and career goals.</li>
            <li>
              Share your view of religious institutions and communities and the
              role you expect to play in them.{" "}
            </li>
            <li>
              What do you believe to be the two most important issues facing
              religious leadership over the next five years, and why.
            </li>
            <li>
              What aspects of this program have led you to make this application
              for admission.
            </li>
          </ol>
        </div>

        {state.essayLoader && (
          <img
            src={`/icons/loading.svg`}
            style={{
              height: "25px",
              marginLeft: "10px",
              backgroundColor: "#000",
            }}
          />
        )}
        <div className="input-group">
          <div className="drag-block">
            <div {...getRootProps()}>
              <input {...getInputProps("test")} />
              {isDragActive ? (
                <p>Drag & Drop a File or Click to Choose</p>
              ) : (
                <p>Drag & Drop a File or Click to Choose</p>
              )}
            </div>
          </div>
          {state.formData.essay && (
            <ul className="multi-images">
              <li>
                <span>
                  {/* {!state.isEssayImage && <h6 className="file-title" alt="Essay">{state.essayFileName}</h6> } */}
                  {state.isEssayImage ? (
                    <img src={state.formData.essay} alt="Essay" />
                  ) : (
                    <img className="doc-icon" src="/icons/file-icon.svg" />
                  )}
                  <div className="thumb_overlay">
                    <div
                      className="thumb_cross"
                      onClick={() => removeFile("essay")}
                    >
                      ✕
                    </div>
                  </div>
                </span>
              </li>
            </ul>
          )}
        </div>
        {state.formData.essay === false && (
          <div className="field-error">Must add essay</div>
        )}
      </div>
    </>
  );
}

export default ApplicationEssay;
